import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NetworkStatus, NetworkStatusService } from '@app/service/network-status.service';

@Component({
    selector: 'app-network-indicator',
    templateUrl: './network-indicator.component.html',
    styleUrls: ['./network-indicator.component.scss']
})
export class NetworkIndicatorComponent implements OnInit, OnDestroy {

    private readonly DIALOG_COOLDOWN = 3600000 * 4; // 1 hora en milisegundos

    status: NetworkStatus;
    private lastDialogTime: number | null = null;
    private destroy$ = new Subject<void>();

    constructor(private networkStatusService: NetworkStatusService) {}

    ngOnInit(): void {
        this.networkStatusService.status$
            .pipe(takeUntil(this.destroy$))
            .subscribe(status => {
                this.checkAndShowDialog(status);
                this.status = status;
            });
    }

    private checkAndShowDialog(newStatus: NetworkStatus): void {
        if (!newStatus?.isOnline) {
            return;
        }

        const isPoorConnection = newStatus.quality === 'deficient' || newStatus.quality === 'poor';

        if (isPoorConnection && this.canShowDialog()) {
            this.lastDialogTime = Date.now();
        }
    }

    private canShowDialog(): boolean {
        if (this.lastDialogTime === null) {
            return true;
        }
        return (Date.now() - this.lastDialogTime) >= this.DIALOG_COOLDOWN;
    }

    get iconClass(): string {
        if (!this.status?.isOnline) {
            return 'pi pi-wifi offline-icon';
        }
        return 'pi pi-wifi';
    }

    get qualityClass(): string {
        if (!this.status) {
            return '';
        }
        switch (this.status.quality) {
            case 'excellent':
                return 'quality-excellent';
            case 'good':
                return 'quality-good';
            case 'deficient':
                return 'quality-deficient';
            case 'poor':
                return 'quality-poor';
            case 'offline':
                return 'quality-offline';
            default:
                return '';
        }
    }

    get tooltipText(): string {
        if (!this.status?.isOnline) {
            return 'Sin conexión a internet';
        }
        if (this.status.apiAvailable === false) {
            return 'No pudimos contactar con Teki. Verifica el estado antes de repetir una operación.';
        }
        if (this.status.latency !== null) {
            return `Conexión: ${this.getQualityLabel()} (${this.status.latency}ms)`;
        }
        return `Conexión: ${this.getQualityLabel()}`;
    }

    get statusText(): string {
        if (this.status?.isOnline && this.status.apiAvailable === null) { return 'Verificando Teki…'; }
        if (this.status?.isOnline && this.status.apiAvailable === false) { return 'Teki sin respuesta'; }
        if (!this.status?.isOnline) {
            return 'Sin conexión';
        }
        switch (this.status.quality) {
            case 'excellent':
                return 'Excelente conexión';
            case 'good':
                return 'Buena conexión';
            case 'deficient':
                return 'Deficiente conexión';
            case 'poor':
                return 'Mala conexión';
            default:
                return 'Verificando...';
        }
    }

    private getQualityLabel(): string {
        switch (this.status?.quality) {
            case 'excellent':
                return 'Excelente';
            case 'good':
                return 'Buena';
            case 'deficient':
                return 'Deficiente';
            case 'poor':
                return 'Mala';
            default:
                return 'Desconocida';
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
